import { useId, useMemo } from 'react'
import type { DialogProps, PaperProps } from '@mui/material'
import { useTitleLabel } from 'lib/hooks/useTitleLabel/useTitleLabel'
import { withConditional, type ModalWithConditionalProps } from './hocs/withConditional'
import { type ModalWithHookedConditionalProps, withHookedConditional } from './hocs/withHookedConditional'
import { ModalHeader } from './components/ModalHeader/ModalHeader'
import { ModalFooter } from './components/ModalFooter/ModalFooter'
import { ModalContent } from './components/ModalContent/ModalContent'
import { ModalPaper } from './components/ModalPaper/ModalPaper'
import { ModalTitleContext } from './components/ModalTitleContext/ModalTitleContext'
import { usePaperSlotProps } from './hooks/usePaperSlotProps/usePaperSlotProps'
import * as $ from './Modal.styled'
import type { ModalProps as ModalStyleProps } from './Modal.types'

export interface ModalProps
  extends Omit<DialogProps, 'TransitionProps'>,
    Pick<ModalWithHookedConditionalProps, '_onConditionalExited'>,
    Pick<ModalWithConditionalProps, '_onConditionalExited'>,
    ModalStyleProps {
  onClose?: DialogProps['onClose']
}

export const Modal: Settle.FC<ModalProps> & {
  Header: typeof ModalHeader
  Footer: typeof ModalFooter
  Content: typeof ModalContent
  Paper: typeof ModalPaper
  withConditional: typeof withConditional
  withHookedConditional: typeof withHookedConditional
} = ({ size = 's', placement, children, _onConditionalExited, slotProps, ...props }) => {
  const titleId = useId()
  const { labelId, registerTitle } = useTitleLabel()
  const titleContext = useMemo(() => ({ titleId, registerTitle }), [titleId, registerTitle])
  const externalPaperSlotProps =
    slotProps?.paper && typeof slotProps.paper === 'object' ? (slotProps.paper as PaperProps) : undefined

  const paperSlotProps = usePaperSlotProps({
    size,
    fullScreen: props.fullScreen,
    placement,
    slotProps: externalPaperSlotProps,
  })

  return (
    <$.Modal
      fullWidth={true}
      aria-labelledby={labelId}
      placement={placement}
      transitionDuration={0}
      slotProps={{
        ...slotProps,
        paper: paperSlotProps,
        transition: {
          ...(typeof slotProps?.transition === 'object' ? slotProps.transition : {}),
          onExited: (node) => {
            const transitionProps = typeof slotProps?.transition === 'object' ? slotProps.transition : undefined

            transitionProps?.onExited?.(node)
            _onConditionalExited?.()
          },
        },
      }}
      {...props}
    >
      <ModalTitleContext.Provider value={titleContext}>{children}</ModalTitleContext.Provider>
    </$.Modal>
  )
}

Modal.Header = ModalHeader
Modal.Footer = ModalFooter
Modal.Content = ModalContent
Modal.Paper = ModalPaper
Modal.withConditional = withConditional
Modal.withHookedConditional = withHookedConditional

export type { ModalWithConditionalProps, ModalWithHookedConditionalProps }
